import { ForeclosureKind, ForeclosureUsecase, UserDefinedRow } from "../../model";
import { DatelyObject, Dates, Numeric, Years } from "../../util";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";
import { RowUsecaseCalcData } from "../RowUsecaseCalcData";

/** Client-side logic for ForeclosureUsecase  */
export class ForeclosureUsecaseLogic extends BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   * @param date The reference date for which the logic is done.
   * Typically the salary date of a calculation and the typically, the information needed is just the year for which the yearly changing numbers are fetched, but may later have more extended features.
   * @param calcData The calculation related data that is required byt the more complex calculations:
   * This data is available only after first round-trip to the server (results object exists).
   */
  public updateUsecase(row: UserDefinedRow, date: DatelyObject, calcData: RowUsecaseCalcData): void {
    const data: ForeclosureUsecase = row.data ?? {};

    if (!data.kind || data.kind === ForeclosureKind.Undefined) {
      data.kind = ForeclosureKind.Fixed;
    }

    if (data.kind !== ForeclosureKind.Periodic && data.kind !== ForeclosureKind.NonPeriodic) {
      return;
    }

    const salaryAfterTax = calcData?.foreclosureSalary || 0;

    if (!data.protectedPortionBase) {
      const numbers = Years.getYearlyChangingNumbers(date);
      data.protectedPortionBase = numbers?.sideCosts?.protectedPortionBase;
      data.protectedPortionDependant =  numbers?.sideCosts?.protectedPortionDependant;
    }


    let otherIncome = Numeric.round(data.incomeFromOtherSources || 0.0, 2);
    let leftToDebtor = Numeric.round(data.additionalAmountLeftToDebtor || 0.0, 2);
    let foreclosure = 0.0;
    if (salaryAfterTax > 0) {
      if (data.kind === ForeclosureKind.NonPeriodic) {
        foreclosure = salaryAfterTax / 3.0;
      } else {
        data.periodLength = this.calculatePeriodLength(calcData?.period.start, calcData?.period.end);
        otherIncome = Numeric.round(data.periodLength * ((otherIncome ?? 0) / 30.0),2);
        leftToDebtor = Numeric.round(data.periodLength * ((leftToDebtor ?? 0) / 30.0), 2) ?? 0;

        const protectedPortion = Numeric.round(
          (data.periodLength || 0) *
          (
            (data.protectedPortionBase || 0.0)
            +
            ((data.dependantsCount || 0) * (data.protectedPortionDependant || 0))
          ), 2) ?? 0;

        const totalSalaryAfterTax = salaryAfterTax + (otherIncome ?? 0);
        if (protectedPortion > totalSalaryAfterTax) {
          foreclosure = 0.0;
        }
        else if (totalSalaryAfterTax <= (protectedPortion * 2.0)) {
          foreclosure = 2.0 * (totalSalaryAfterTax - protectedPortion) / 3.0;
        }
        else if (totalSalaryAfterTax <= (protectedPortion * 4.0)) {
          foreclosure = totalSalaryAfterTax / 3.0;
        }
        else {
          foreclosure = (protectedPortion * 4.0) / 3.0 +
            4.0 * (totalSalaryAfterTax - protectedPortion * 4.0) / 5.0;
          foreclosure = Math.min(foreclosure, totalSalaryAfterTax / 2.0);
        }
        foreclosure = Math.max(0.0, Math.min(foreclosure, salaryAfterTax) - leftToDebtor);
      }
    }
    row.count = 1;
    row.price = Numeric.round(foreclosure, 2);

  }

  /** Gets validation message for UnionPaymentUsecase  */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage | undefined {
    const data: ForeclosureUsecase = row.data || {};
    if (data.kind === ForeclosureKind.NonPeriodic || data.kind === ForeclosureKind.Periodic ) {
      return {
        type: "default",
        msg: "SALAXY.VALIDATION.Usecases.foreclosure.default",
      };
    } else {
      if (!row.price) {
        return {
          type: "error",
          msg: "SALAXY.VALIDATION.Usecases.foreclosure.noPrice",
        };
      }
    }
    return undefined;
  }

  private calculatePeriodLength(startDate?: string | null, endDate?: string | null): number {
    if (startDate != null && endDate != null) {
      if (this.isOnceAMonthPeriod(startDate, endDate)) {
        return 30;
      }
      else if (this.isEvery2WeeksPeriod(startDate, endDate)) {
        return 14;
      }
      else if (this.isTwiceAMonthPeriod(startDate, endDate)) {
        return 15;
      } else {
        return Math.max(0, this.getDays(startDate, endDate));
      }
    }
    else {
      return 30;
    }
  }

  private isOnceAMonthPeriod(startDate: string, endDate: string): boolean {
    return endDate === Dates.add(Dates.add(startDate, 1, "month") ?? "",-1, "day");
  }


  private isTwiceAMonthPeriod(startDate: string, endDate: string): boolean {
    return (Dates.getDuration(startDate, endDate).asDays() + 1 === 15)
    ||
    (Dates.getDay(startDate) === 15 && endDate === Dates.startEnd(startDate,"end-month"))
    ||
    (Dates.getDay(startDate) === 16 && endDate === Dates.startEnd(startDate,"end-month"));
  }

  private isEvery2WeeksPeriod(startDate: string, endDate: string): boolean {
    return endDate === Dates.add(Dates.add(startDate,2,"week") ?? "",-1, "day");
  }

  private getDays(startDate: string, endDate: string): number {
    return  Dates.getDuration(startDate, endDate).asDays() + 1;
  }
}
